import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress,
  Tabs,
  Tab,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
} from '@mui/material';
import {
  Close,
  Security,
  Gavel,
  Shield,
  Warning,
  CheckCircle,
  Error,
  Info,
  AssessmentOutlined,
  PictureAsPdf,
  Link as LinkIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState, AppDispatch } from '../store/store';
import {
  createDeepResearchReport,
  getDeepResearchReport,
  setCurrentReport,
  setPollingReportId,
  clearError,
  DeepResearchRequest,
} from '../store/slices/deepResearchSlice';
import { SearchResult } from '../store/slices/searchSlice';
import { fraudUtils, deepResearchAPI } from '../services/api';
import ReactMarkdown from 'react-markdown';

interface DeepResearchModalProps {
  open: boolean;
  onClose: () => void;
  contract: SearchResult | null;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`deep-research-tabpanel-${index}`}
      aria-labelledby={`deep-research-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
};

const DeepResearchModal: React.FC<DeepResearchModalProps> = ({ open, onClose, contract }) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { currentReport, loading, pollingReportId, error } = useSelector(
    (state: RootState) => state.deepResearch
  );
  
  const [tabValue, setTabValue] = useState(0);
  const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(null);
  const [showProcessingMessage, setShowProcessingMessage] = useState(false);

  // Start polling for report updates when a report is pending
  useEffect(() => {
    if (pollingReportId && open) {
      const interval = setInterval(() => {
        dispatch(getDeepResearchReport(pollingReportId));
      }, 3000); // Poll every 3 seconds
      
      setPollingInterval(interval);
      
      return () => {
        clearInterval(interval);
        setPollingInterval(null);
      };
    } else if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
  }, [pollingReportId, dispatch, open]);

  // Clean up on modal close
  useEffect(() => {
    if (!open) {
      if (pollingInterval) {
        clearInterval(pollingInterval);
        setPollingInterval(null);
      }
      dispatch(setPollingReportId(null));
      dispatch(setCurrentReport(null));
      dispatch(clearError());
      setTabValue(0);
    }
  }, [open, dispatch, pollingInterval]);

  const handleInitiateDeepResearch = () => {
    if (!contract) return;

    const request: DeepResearchRequest = {
      contract_id: contract.id || contract.award_details?.award_id || `contract_${Date.now()}`,
      vendor_name: contract.vendor || contract.vendor_name || 'Unknown Vendor',
      vendor_uei: contract.award_details?.award_id || undefined,
      award_amount: contract.amount || contract.total_obligated_amount || 0,
      award_date: contract.date || contract.award_details?.start_date || new Date().toISOString(),
      awarding_agency: contract.agency || contract.award_details?.contracting_agency || 'Unknown Agency',
      competition_type: contract.solicitation_info?.competition_type || undefined,
      number_of_offers_received: contract.number_of_offers_received || undefined,
      naics_code: contract.award_details?.award_type || undefined,
      contract_description: contract.title || contract.description || contract.prime_award_base_transaction_description || 'No description available',
      additional_data: {
        fraud_risk_level: contract.fraud_risk_level,
        fraud_risk_message: contract.fraud_risk_message,
        fraud_risk_factors: contract.fraud_risk_factors,
        location: contract.location,
        url: contract.url || contract.usaspending_permalink,
      },
    };

    dispatch(createDeepResearchReport(request))
      .unwrap()
      .then((result) => {
        // Show processing message
        setShowProcessingMessage(true);
        
        // Auto-close the modal after 5 seconds
        setTimeout(() => {
          setShowProcessingMessage(false);
          onClose();
        }, 5000);
      })
      .catch((error) => {
        console.error('Failed to create deep research report:', error);
      });
  };

  const handleViewReports = () => {
    navigate('/investigator/deep-research-reports');
    setShowProcessingMessage(false);
    onClose();
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleExportPdf = async () => {
    if (!currentReport?.id) return;

    try {
      const blob = await deepResearchAPI.exportToPdf(currentReport.id);
      
      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `deep-research-report-${currentReport.id.substring(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export PDF:', error);
      // You could add a toast notification here for better UX
    }
  };

  const getRiskColor = (riskLevel: string) => {
    switch (riskLevel?.toUpperCase()) {
      case 'CRITICAL':
        return '#d32f2f';
      case 'HIGH':
        return '#f57c00';
      case 'MEDIUM':
        return '#fbc02d';
      case 'LOW':
        return '#4caf50';
      case 'MINIMAL':
        return '#9e9e9e';
      default:
        return '#9e9e9e';
    }
  };

  const getRiskIcon = (riskLevel: string) => {
    switch (riskLevel?.toUpperCase()) {
      case 'CRITICAL':
        return <Error sx={{ color: '#d32f2f' }} />;
      case 'HIGH':
        return <Warning sx={{ color: '#f57c00' }} />;
      case 'MEDIUM':
        return <Info sx={{ color: '#fbc02d' }} />;
      case 'LOW':
        return <CheckCircle sx={{ color: '#4caf50' }} />;
      case 'MINIMAL':
        return <CheckCircle sx={{ color: '#9e9e9e' }} />;
      default:
        return <Info sx={{ color: '#9e9e9e' }} />;
    }
  };

  const formatInvestigationDetails = (text: string) => {
    // Convert markdown links [text](url) to proper React elements
    const parts: (string | JSX.Element)[] = [];
    let lastIndex = 0;
    
    // Regular expression to match markdown links
    const linkRegex = /\[([^\]]+)\]\(([^\)]+)\)/g;
    let match;
    
    while ((match = linkRegex.exec(text)) !== null) {
      // Add text before the link
      if (match.index > lastIndex) {
        const textBefore = text.substring(lastIndex, match.index);
        parts.push(...formatTextSegment(textBefore));
      }
      
      // Add the link
      const linkText = match[1];
      const url = match[2];
      parts.push(
        <a key={`link-${match.index}`} href={url} target="_blank" rel="noopener noreferrer">
          {linkText}
        </a>
      );
      
      lastIndex = match.index + match[0].length;
    }
    
    // Add remaining text after last link
    if (lastIndex < text.length) {
      const remainingText = text.substring(lastIndex);
      parts.push(...formatTextSegment(remainingText));
    }
    
    // Break into paragraphs at sentence boundaries
    const paragraphs: JSX.Element[] = [];
    let currentParagraph: (string | JSX.Element)[] = [];
    
    parts.forEach((part, index) => {
      currentParagraph.push(part);
      
      // Check if this part ends a sentence and next part starts a new one
      if (typeof part === 'string' && part.match(/\.\s*$/)) {
        // Look ahead to see if next part starts with capital letter
        const nextPart = parts[index + 1];
        if (nextPart && typeof nextPart === 'string' && nextPart.match(/^[A-Z]/)) {
          paragraphs.push(
            <Typography key={`para-${paragraphs.length}`} variant="body2" paragraph>
              {currentParagraph}
            </Typography>
          );
          currentParagraph = [];
        }
      }
    });
    
    // Add any remaining content
    if (currentParagraph.length > 0) {
      paragraphs.push(
        <Typography key={`para-${paragraphs.length}`} variant="body2" paragraph>
          {currentParagraph}
        </Typography>
      );
    }
    
    return <>{paragraphs}</>;
  };
  
  const formatTextSegment = (text: string): (string | JSX.Element)[] => {
    const segments: (string | JSX.Element)[] = [];
    
    // Highlight dollar amounts
    const dollarRegex = /(\$[\d,]+\.?\d*)/g;
    let lastIndex = 0;
    let match;
    
    while ((match = dollarRegex.exec(text)) !== null) {
      // Add text before the dollar amount
      if (match.index > lastIndex) {
        segments.push(text.substring(lastIndex, match.index));
      }
      
      // Add the highlighted dollar amount
      segments.push(
        <strong key={`dollar-${match.index}`} style={{ color: '#2e7d32' }}>
          {match[1]}
        </strong>
      );
      
      lastIndex = match.index + match[0].length;
    }
    
    // Add remaining text
    if (lastIndex < text.length) {
      segments.push(text.substring(lastIndex));
    }
    
    return segments.length > 0 ? segments : [text];
  };

  const renderReportContent = (content: string, icon: React.ReactNode, title: string) => {
    // Try to parse content as JSON first
    let parsedContent: any = null;
    try {
      parsedContent = typeof content === 'string' ? JSON.parse(content) : content;
      
      // Check if the parsed content has raw_content wrapper
      if (parsedContent && parsedContent.raw_content) {
        // Extract JSON from raw_content if it contains JSON
        const rawContent = parsedContent.raw_content;
        if (rawContent.includes('```json')) {
          const jsonMatch = rawContent.match(/```json\s*([\s\S]*?)\s*```/);
          if (jsonMatch && jsonMatch[1]) {
            try {
              parsedContent = JSON.parse(jsonMatch[1]);
            } catch (e) {
              // Keep as is if can't parse
            }
          }
        } else {
          // Try parsing raw_content directly
          try {
            parsedContent = JSON.parse(rawContent);
          } catch (e) {
            // Keep raw_content as string
            parsedContent = { content: rawContent };
          }
        }
      }
    } catch (e) {
      // If not JSON, treat as markdown/plain text
      parsedContent = null;
    }

    return (
      <Paper sx={{ p: 3, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          {icon}
          <Typography variant="h6" sx={{ ml: 1 }}>
            {title}
          </Typography>
        </Box>
        
        {parsedContent ? (
          <Box>
            {/* Render JSON report content */}
            {parsedContent.summary && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Summary
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {parsedContent.summary}
                </Typography>
              </Box>
            )}
            
            {parsedContent.key_points && parsedContent.key_points.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Key Points
                </Typography>
                <Box component="ul" sx={{ pl: 2 }}>
                  {parsedContent.key_points.map((point: string, index: number) => (
                    <Typography component="li" variant="body2" key={index} sx={{ mb: 0.5 }}>
                      {point}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}
            
            {parsedContent.risk_indicators && parsedContent.risk_indicators.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: 'error.main' }}>
                  Risk Indicators
                </Typography>
                <Box component="ul" sx={{ pl: 2 }}>
                  {parsedContent.risk_indicators.map((indicator: string, index: number) => (
                    <Typography component="li" variant="body2" key={index} sx={{ mb: 0.5, color: 'error.dark' }}>
                      {indicator}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}
            
            {parsedContent.recommendations && parsedContent.recommendations.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
                  Recommendations
                </Typography>
                <Box component="ul" sx={{ pl: 2 }}>
                  {parsedContent.recommendations.map((rec: string, index: number) => (
                    <Typography component="li" variant="body2" key={index} sx={{ mb: 0.5 }}>
                      {rec}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}
            
            {/* Judge-specific fields */}
            {parsedContent.verdict && (
              <Box sx={{ mb: 3, p: 2, bgcolor: parsedContent.verdict.toLowerCase().includes('legitimate') ? 'success.50' : 'error.50', borderRadius: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: parsedContent.verdict.toLowerCase().includes('legitimate') ? 'success.main' : 'error.main' }}>
                  VERDICT: {parsedContent.verdict}
                </Typography>
              </Box>
            )}
            
            {parsedContent.findings && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Key Findings
                </Typography>
                <Box sx={{ pl: 2 }}>
                  {Object.entries(parsedContent.findings).map(([key, value]: [string, any]) => (
                    <Typography variant="body2" key={key} sx={{ mb: 0.5 }}>
                      <strong>{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:</strong> {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value)}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}
            
            {parsedContent.irregularities && parsedContent.irregularities.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: 'error.main' }}>
                  Irregularities Identified
                </Typography>
                <Box sx={{ pl: 2 }}>
                  {parsedContent.irregularities.map((irr: any, index: number) => (
                    <Box key={index} sx={{ mb: 1.5 }}>
                      {typeof irr === 'object' ? (
                        <>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: irr.severity === 'HIGH' || irr.severity === 'CRITICAL' ? 'error.main' : 'warning.main' }}>
                            {irr.type} - Severity: {irr.severity}
                          </Typography>
                          {irr.description && (
                            <Typography variant="body2" sx={{ ml: 2, color: 'text.secondary' }}>
                              {irr.description}
                            </Typography>
                          )}
                        </>
                      ) : (
                        <Typography variant="body2" sx={{ color: 'error.dark' }}>
                          • {String(irr)}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
            
            {parsedContent.recommended_actions && parsedContent.recommended_actions.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
                  Recommended Actions
                </Typography>
                <Box sx={{ pl: 2 }}>
                  {parsedContent.recommended_actions.map((action: any, index: number) => (
                    <Box key={index} sx={{ mb: 1 }}>
                      {typeof action === 'object' ? (
                        <>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: action.priority === 'HIGH' ? 'error.main' : action.priority === 'MEDIUM' ? 'warning.main' : 'info.main' }}>
                            [{action.priority}] {action.action}
                          </Typography>
                          {action.reason && (
                            <Typography variant="body2" sx={{ ml: 2, fontStyle: 'italic', color: 'text.secondary' }}>
                              {action.reason}
                            </Typography>
                          )}
                        </>
                      ) : (
                        <Typography variant="body2">
                          • {String(action)}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
            
            {parsedContent.final_determination && (
              <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Final Determination
                </Typography>
                <Typography variant="body2">
                  {parsedContent.final_determination}
                </Typography>
              </Box>
            )}
            
            {parsedContent.investigation_details && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Investigation Details
                </Typography>
                <Box sx={{ 
                  '& p': { mb: 1.5 },
                  '& a': { 
                    color: 'primary.main', 
                    textDecoration: 'underline',
                    '&:hover': { textDecoration: 'none' }
                  },
                  '& strong': { fontWeight: 600 },
                  bgcolor: 'grey.50',
                  p: 2,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'grey.200'
                }}>
                  {formatInvestigationDetails(parsedContent.investigation_details)}
                </Box>
              </Box>
            )}
            
            {parsedContent.reasoning && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Judicial Reasoning
                </Typography>
                <Typography variant="body2">
                  {parsedContent.reasoning}
                </Typography>
              </Box>
            )}
            
            {parsedContent.risk_score !== undefined && (
              <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2">
                  <strong>Risk Score:</strong> {parsedContent.risk_score}/100
                </Typography>
                {parsedContent.risk_level && (
                  <Typography variant="body2" sx={{ color: getRiskColor(parsedContent.risk_level) }}>
                    <strong>Risk Level:</strong> {parsedContent.risk_level}
                  </Typography>
                )}
              </Box>
            )}
            
            {parsedContent.confidence_score !== undefined && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Confidence Score:</strong> {parsedContent.confidence_score}%
                </Typography>
              </Box>
            )}
            
            {parsedContent.severity && (
              <Box sx={{ mt: 2 }}>
                <Chip
                  label={`Severity: ${parsedContent.severity}`}
                  sx={{
                    bgcolor: parsedContent.severity === 'critical' ? 'error.main' : 
                             parsedContent.severity === 'high' ? 'warning.main' : 'info.main',
                    color: 'white'
                  }}
                />
              </Box>
            )}
          </Box>
        ) : (
          // Fallback to markdown rendering if not JSON
          <Box sx={{
            '& p': { mb: 1 },
            '& ul': { pl: 2, mb: 1 },
            '& ol': { pl: 2, mb: 1 },
            '& li': { mb: 0.5 },
            '& strong': { fontWeight: 600 },
            '& h1, & h2, & h3, & h4, & h5, & h6': { 
              fontWeight: 600, 
              mt: 1.5, 
              mb: 1,
              color: 'primary.main'
            }
          }}>
            <ReactMarkdown>{content}</ReactMarkdown>
          </Box>
        )}
      </Paper>
    );
  };

  const renderProcessingMessage = () => (
    <Box sx={{ textAlign: 'center', py: 6 }}>
      <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 3 }} />
      <Typography variant="h5" gutterBottom color="success.main">
        Deep Research Analysis Initiated
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}>
        Deep research analysis initiated. This comprehensive analysis typically takes 10-20 minutes as our AI researches multiple sources and generates detailed reports.
      </Typography>
      
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
        <Button
          variant="contained"
          size="large"
          onClick={handleViewReports}
          startIcon={<AssessmentOutlined />}
          sx={{ minWidth: 250 }}
        >
          View Deep Research Reports
        </Button>
        
        <Typography variant="body2" color="text.secondary">
          You can track the progress of your analysis in the reports section
        </Typography>
      </Box>

      <Box sx={{ mt: 4, p: 3, bgcolor: 'info.50', borderRadius: 2, border: '1px solid', borderColor: 'info.200' }}>
        <Typography variant="body2" color="info.main" sx={{ fontWeight: 600, mb: 1 }}>
          What happens next:
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          • AI analysts will examine the contract from multiple perspectives
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          • Advocate, disputant, and evaluator reports will be generated
        </Typography>
        <Typography variant="body2" color="text.secondary">
          • You'll receive a comprehensive fraud risk assessment with detailed analysis
        </Typography>
      </Box>
    </Box>
  );

  const renderLoadingState = () => (
    <Box sx={{ textAlign: 'center', py: 6 }}>
      <CircularProgress size={60} sx={{ mb: 3 }} />
      <Typography variant="h6" gutterBottom>
        Generating Deep Research Report
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Our AI investigators are analyzing the contract from multiple perspectives. This typically takes 5-10 seconds.
      </Typography>
      <LinearProgress sx={{ maxWidth: 400, mx: 'auto' }} />
      
      <Box sx={{ mt: 4, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
        <Typography variant="body2" color="text.secondary">
          <strong>Analysis in Progress:</strong>
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          • Advocate perspective - Legitimate explanations for contract patterns
        </Typography>
        <Typography variant="body2" color="text.secondary">
          • Disputant perspective - Potential fraud indicators and red flags
        </Typography>
        <Typography variant="body2" color="text.secondary">
          • Evaluator assessment - Balanced risk evaluation and recommendations
        </Typography>
      </Box>
    </Box>
  );

  const renderContractSummary = () => {
    if (!contract && !currentReport) return null;

    // Use currentReport data if available, fallback to contract data
    const data: any = currentReport || contract;
    if (!data) return null;

    return (
      <Paper sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
          Contract/Award Information
        </Typography>
        
        {/* Primary Information */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
            Primary Details
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 2 }}>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Contract/Award ID:</strong> {data.contract_id || data.id || (data as any).award_details?.award_id || 'N/A'}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Description:</strong> {data.contract_description || data.title || data.description || 'N/A'}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Vendor Name:</strong> {data.vendor_name || data.vendor || 'N/A'}
              </Typography>
              {data.vendor_uei && (
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Vendor UEI:</strong> {data.vendor_uei}
                </Typography>
              )}
            </Box>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Award Amount:</strong> {fraudUtils.formatCurrency(data.award_amount || data.amount || data.total_obligated_amount || 0)}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Awarding Agency:</strong> {data.agency_name || data.agency || data.awarding_agency || 'N/A'}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Award Date:</strong> {data.award_date ? new Date(data.award_date).toLocaleDateString() : 'N/A'}
              </Typography>
              {data.period_of_performance_start && (
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Performance Period:</strong> {new Date(data.period_of_performance_start).toLocaleDateString()} - {data.period_of_performance_end ? new Date(data.period_of_performance_end).toLocaleDateString() : 'Ongoing'}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        {/* Competition Information */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
            Competition Details
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 2 }}>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Competition Type:</strong> {data.competition_type || data.extent_competed || 'N/A'}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Number of Offers:</strong> {data.number_of_offers_received ?? 'N/A'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>NAICS Code:</strong> {data.naics_code || 'N/A'}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Place of Performance:</strong> {data.place_of_performance || data.pop_city || 'N/A'}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Additional Details if available */}
        {(data.contract_title || data.solicitation_id || data.modifications) && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
              Additional Information
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 2 }}>
              <Box>
                {data.contract_title && (
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Contract Title:</strong> {data.contract_title}
                  </Typography>
                )}
                {data.solicitation_id && (
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Solicitation ID:</strong> {data.solicitation_id}
                  </Typography>
                )}
                {data.modifications && (
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Modifications:</strong> {data.modifications}
                  </Typography>
                )}
              </Box>
              <Box>
                {data.contract_type && (
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Contract Type:</strong> {data.contract_type}
                  </Typography>
                )}
                {data.set_aside_type && (
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Set-Aside Type:</strong> {data.set_aside_type}
                  </Typography>
                )}
                {data.funding_agency && (
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Funding Agency:</strong> {data.funding_agency}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        )}

        {/* Source Links */}
        {(data.metadata?.urls || contract?.url || contract?.usaspending_permalink) && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
              Source Links
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {data.metadata?.urls?.usaspending && (
                <a 
                  href={data.metadata.urls.usaspending} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ color: '#1976d2', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}
                >
                  USAspending.gov <LinkIcon style={{ fontSize: 14 }} />
                </a>
              )}
              {data.metadata?.urls?.sam_gov && (
                <a 
                  href={data.metadata.urls.sam_gov} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ color: '#1976d2', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}
                >
                  SAM.gov <LinkIcon style={{ fontSize: 14 }} />
                </a>
              )}
              {(contract?.url || contract?.usaspending_permalink) && (
                <a 
                  href={contract.url || contract.usaspending_permalink} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ color: '#1976d2', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}
                >
                  View Original <LinkIcon style={{ fontSize: 14 }} />
                </a>
              )}
            </Box>
          </Box>
        )}

        {/* Risk Assessment if available */}
        {(data.fraud_risk_level || data.risk_level) && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'white', borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
              Risk Assessment
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip
                icon={getRiskIcon(data.fraud_risk_level || data.risk_level)}
                label={`Risk Level: ${data.fraud_risk_level || data.risk_level}`}
                sx={{
                  bgcolor: getRiskColor(data.fraud_risk_level || data.risk_level) + '20',
                  color: getRiskColor(data.fraud_risk_level || data.risk_level),
                  fontWeight: 600,
                }}
              />
              {data.fraud_risk_score !== undefined && (
                <Typography variant="body1" sx={{ fontWeight: 600, color: getRiskColor(data.fraud_risk_level || data.risk_level) }}>
                  Score: {data.fraud_risk_score}/100
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </Paper>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { minHeight: '70vh', maxHeight: '90vh' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Security sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6">
            Deep Research Analysis
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => dispatch(clearError())}>
            {error}
          </Alert>
        )}

        {renderContractSummary()}

        {showProcessingMessage ? (
          renderProcessingMessage()
        ) : !currentReport && !loading ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Security sx={{ fontSize: 80, color: 'primary.main', mb: 3 }} />
            <Typography variant="h5" gutterBottom>
              Deep Fraud Analysis
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}>
              Generate a comprehensive fraud risk assessment from three investigative perspectives:
              Defense, Prosecution, and Judicial review. Our AI will analyze contract patterns,
              vendor history, competition data, and risk indicators.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={handleInitiateDeepResearch}
              disabled={loading}
              startIcon={<Security />}
            >
              Start Deep Research Analysis
            </Button>
          </Box>
        ) : currentReport?.status === 'pending' || loading ? (
          renderLoadingState()
        ) : currentReport?.status === 'error' ? (
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>Analysis Failed</Typography>
            {currentReport.error_message || 'An error occurred during the analysis.'}
          </Alert>
        ) : currentReport?.status === 'completed' ? (
          <>
            {/* Risk Summary */}
            <Paper sx={{ p: 3, mb: 3, bgcolor: 'primary.50' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">Final Risk Assessment</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Chip
                    icon={getRiskIcon(currentReport.risk_level || 'UNKNOWN')}
                    label={`${currentReport.risk_level || 'UNKNOWN'} RISK`}
                    sx={{
                      bgcolor: getRiskColor(currentReport.risk_level || 'UNKNOWN') + '20',
                      color: getRiskColor(currentReport.risk_level || 'UNKNOWN'),
                      fontWeight: 600,
                      fontSize: '1rem',
                      px: 1,
                    }}
                  />
                  <Typography variant="h4" sx={{ fontWeight: 600, color: getRiskColor(currentReport.risk_level || 'UNKNOWN') }}>
                    {currentReport.fraud_risk_score || 0}/100
                  </Typography>
                </Box>
              </Box>
              <LinearProgress
                variant="determinate"
                value={currentReport.fraud_risk_score || 0}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  '& .MuiLinearProgress-bar': {
                    bgcolor: getRiskColor(currentReport.risk_level || 'UNKNOWN'),
                  }
                }}
              />
            </Paper>

            {/* Report Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <Tabs value={tabValue} onChange={handleTabChange}>
                <Tab
                  icon={<Shield />}
                  iconPosition="start"
                  label="Defense Perspective"
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                />
                <Tab
                  icon={<Gavel />}
                  iconPosition="start"
                  label="Prosecution Perspective"
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                />
                <Tab
                  icon={<Security />}
                  iconPosition="start"
                  label="Judicial Assessment"
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                />
              </Tabs>
            </Box>

            <TabPanel value={tabValue} index={0}>
              {currentReport.defense_report &&
                renderReportContent(
                  currentReport.defense_report,
                  <Shield sx={{ color: '#4caf50' }} />,
                  'Advocate Perspective - Legitimate Explanations'
                )}
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              {currentReport.prosecution_report &&
                renderReportContent(
                  currentReport.prosecution_report,
                  <Gavel sx={{ color: '#f57c00' }} />,
                  'Disputant Perspective - Fraud Indicators'
                )}
            </TabPanel>

            <TabPanel value={tabValue} index={2}>
              {currentReport.judge_report &&
                renderReportContent(
                  currentReport.judge_report,
                  <Security sx={{ color: 'primary.main' }} />,
                  'Evaluator Assessment - Final Determination'
                )}
            </TabPanel>
          </>
        ) : null}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <Box>
          {currentReport?.status === 'completed' && (
            <Button
              onClick={handleExportPdf}
              startIcon={<PictureAsPdf />}
              variant="outlined"
              color="primary"
            >
              Download PDF
            </Button>
          )}
        </Box>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeepResearchModal;